import { createFileRoute, Link, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { useGetServiceDetail, useGetPublicServiceDetail, useDeleteService } from '../api/services';
import { getProfileIdentifier } from '../utils/profileUrl';
import { copyTextToClipboard } from '../utils/copyToClipboard';
import { toast } from '../utils/toast';
import { formatTagLabel } from '../utils/tagLabel';
import { formatServicePrice } from '../utils/price';
import {
  FiArrowLeft,
  FiUser,
  FiBriefcase,
  FiCopy,
  FiEdit2,
  FiChevronRight,
  FiChevronLeft,
  FiAlertCircle,
  FiTrash2,
  FiExternalLink,
  FiMail,
  FiX,
} from 'react-icons/fi';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { ApplyServiceModal, type ApplyServiceData } from '../components/services/ApplyServiceModal';

const SERVICE_IMAGE_FALLBACK_SRC = '/Musicade-Logo.png';

type ServiceImageProps = {
  src: string;
  alt: string;
  className: string;
  fallbackClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
  draggable?: boolean;
};

function ServiceImage({
  src,
  alt,
  className,
  fallbackClassName = 'w-full h-full object-contain p-6',
  onClick,
  draggable,
}: ServiceImageProps) {
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    setHasFailed(false);
  }, [src]);

  return (
    <img
      src={hasFailed || !src.trim() ? SERVICE_IMAGE_FALLBACK_SRC : src}
      alt={hasFailed ? `${alt} fallback` : alt}
      className={hasFailed ? fallbackClassName : className}
      onClick={onClick}
      draggable={draggable}
      onError={() => {
        setHasFailed(true);
      }}
    />
  );
}

export const Route = createFileRoute('/services/$serviceId')({
  component: ServiceDetail,
});

function ServiceDetail() {
  const { serviceId } = Route.useParams();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();

  // Always call both hooks before any early return; only one fetches based on auth state
  const authServiceResult = useGetServiceDetail(isAuthenticated ? serviceId : '');
  const publicServiceResult = useGetPublicServiceDetail(!isAuthenticated ? serviceId : '');
  const {
    data: service,
    isLoading,
    isError,
  } = isAuthenticated ? authServiceResult : publicServiceResult;
  const { mutate: deleteService, isPending: isDeleting } = useDeleteService();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      const total = service?.images?.length ?? 0;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight')
        setLightboxIndex((p) => (p !== null && p < total - 1 ? p + 1 : p));
      if (e.key === 'ArrowLeft') setLightboxIndex((p) => (p !== null && p > 0 ? p - 1 : p));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex]);

  // Render the edit child route when on /services/:id/edit
  if (routerState.location.pathname.endsWith('/edit')) {
    return <Outlet />;
  }

  const handleOpenApply = () => {
    if (!service) return;
    setIsApplyModalOpen(true);
  };

  const handleDelete = () => {
    deleteService(Number(serviceId), {
      onSuccess: () => {
        setIsDeleteConfirming(false);
        navigate({ to: '/services' });
      },
      onError: () => {
        setIsDeleteConfirming(false);
      },
    });
  };

  const handleCopyLink = async () => {
    const copied = await copyTextToClipboard(window.location.href);

    if (copied) {
      toast.success('Link copied');
      return;
    }

    toast.error('Unable to copy link. Please copy manually.');
  };

  if (isLoading) {
    return <LoadingLogo />;
  }

  if (isError || !service) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[60vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Service not found</h2>
          <p className="text-zinc-500 mb-6">
            This service may have been removed or you don't have access.
          </p>
          <Button
            onClick={() => navigate({ to: '/services' })}
            size="lg"
            className="rounded-full px-8"
          >
            Back to Services
          </Button>
        </main>
        <BottomNav />
      </div>
    );
  }

  const currentUserId = user?.id ?? localStorage.getItem('user_id') ?? '';
  const creator = service.creator;
  const isOwner =
    !!currentUserId &&
    (creator?.id
      ? String(creator.id) === String(currentUserId)
      : !!service.user && String(service.user) === String(currentUserId));

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb + actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 mt-2">
          <div className="flex items-center gap-2 text-zinc-500 text-[13px] sm:text-[14px] overflow-x-auto no-scrollbar whitespace-nowrap">
            <button
              onClick={() => window.history.back()}
              className="hover:text-white transition-colors shrink-0 p-1 -ml-1 rounded-full hover:bg-white/5"
            >
              <FiArrowLeft size={20} />
            </button>
            <FiBriefcase size={15} className="shrink-0" />
            <Link
              to="/services"
              className="hover:text-white transition-colors font-medium shrink-0"
            >
              Services
            </Link>
            <span className="text-zinc-700 shrink-0">›</span>
            <span className="text-zinc-400 truncate min-w-0 max-w-[40vw] sm:max-w-xs">
              {service.title}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              className="rounded-full gap-2 px-5 bg-zinc-900 border-white/5 hover:bg-white/5"
              onClick={handleCopyLink}
            >
              <FiCopy size={15} />
              <span>Copy Link</span>
            </Button>
            {isOwner && (
              <Link to="/services/$serviceId/edit" params={{ serviceId }}>
                <Button
                  variant="secondary"
                  className="rounded-full gap-2 px-5 bg-zinc-900 border-white/5 hover:bg-white/5"
                >
                  <FiEdit2 size={15} />
                  <span>Edit</span>
                </Button>
              </Link>
            )}
            {isOwner && (
              <Button
                variant="secondary"
                className="rounded-full gap-2 px-5 bg-zinc-900 border-white/5 text-red-500 hover:bg-red-500/10"
                onClick={() => setIsDeleteConfirming(true)}
              >
                <FiTrash2 size={15} />
                <span>Delete</span>
              </Button>
            )}
            {!isOwner && (
              <Button
                size="sm"
                className="rounded-full px-5 bg-blue-600 hover:bg-blue-500 text-white font-semibold"
                onClick={handleOpenApply}
              >
                Contact
              </Button>
            )}
          </div>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12">
          {/* Left: image grid */}
          <div className="w-full lg:w-80 shrink-0 flex flex-col gap-3">
            {service.images.length > 0 ? (
              <>
                <div
                  className="relative w-full aspect-square rounded-2xl overflow-hidden bg-zinc-900 border border-white/5 cursor-pointer"
                  onClick={() => setLightboxIndex(0)}
                >
                  <ServiceImage
                    src={service.images[0]}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    fallbackClassName="w-full h-full object-contain p-10"
                  />
                </div>
                {service.images.length > 1 && (
                  <div className="grid grid-cols-3 gap-2">
                    {service.images.slice(1, 4).map((img, i) => (
                      <div
                        key={i}
                        className="relative aspect-square rounded-xl overflow-hidden bg-zinc-900 border border-white/5 cursor-pointer"
                        onClick={() => setLightboxIndex(i + 1)}
                      >
                        <ServiceImage src={img} alt="" className="w-full h-full object-cover" />
                        {i === 2 && service.images.length > 4 && (
                          <div
                            className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm font-semibold"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxIndex(4);
                            }}
                          >
                            +{service.images.length - 4}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full aspect-square rounded-2xl overflow-hidden bg-zinc-900 border border-white/5 flex items-center justify-center text-zinc-700">
                <FiBriefcase size={56} />
              </div>
            )}
          </div>

          {/* Right: content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h1 className="text-white text-3xl sm:text-4xl font-bold leading-tight mb-3">
              {service.title}
            </h1>

            {/* Price */}
            {service.price && (
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[#a78bfa] font-semibold text-lg">
                  {formatServicePrice(service.price)}
                </span>
              </div>
            )}

            <div className="w-full h-px bg-white/5 mb-6" />

            {/* Description */}
            {service.description && (
              <div className="mb-8">
                <h2 className="text-white text-xl font-semibold mb-3">Description</h2>
                <p className="text-zinc-400 text-[15px] leading-relaxed whitespace-pre-line">
                  {service.description}
                </p>
              </div>
            )}

            {/* User types */}
            {service.user_types.length > 0 && (
              <div className="mb-6">
                <h2 className="text-white text-base font-semibold mb-3">Artist Types</h2>
                <div className="flex flex-wrap gap-2">
                  {service.user_types.map((ut) => (
                    <span
                      key={ut.id}
                      className="px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-400 text-[12px] font-medium"
                    >
                      {ut.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {service.user_tags.length > 0 && (
              <div className="mb-6">
                <h2 className="text-white text-base font-semibold mb-3">Tags</h2>
                <div className="flex flex-wrap gap-2">
                  {service.user_tags.map((tag, i) => (
                    <span
                      key={tag.id || i}
                      className="px-3 py-1.5 rounded-full bg-zinc-800 border border-white/8 text-zinc-300 text-[12px] font-medium"
                    >
                      {formatTagLabel(tag.name)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Work sample links */}
            {service.work_example_links.length > 0 && (
              <div className="mb-8">
                <h2 className="text-white text-base font-semibold mb-3">Work Samples</h2>
                <div className="flex flex-col gap-2">
                  {service.work_example_links.filter(Boolean).map((link, i) => (
                    <a
                      key={i}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-[14px] transition-colors"
                    >
                      <FiExternalLink size={14} />
                      <span className="truncate">{link}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Created at */}
            {service.created_at && (
              <p className="text-zinc-600 text-[12px] pt-4 border-t border-white/5">
                Created:{' '}
                {new Intl.DateTimeFormat('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }).format(new Date(service.created_at))}
              </p>
            )}

            {/* Creator card — visible to non-owners only */}
            {!isOwner && creator && creator.id && (
              <div className="mt-8 pt-6 border-t border-white/5">
                <p className="text-zinc-500 text-[12px] uppercase tracking-wider font-medium mb-3">
                  Service Provider
                </p>
                <div
                  onClick={() =>
                    navigate({
                      to: '/profile/$identifier',
                      params: {
                        identifier: getProfileIdentifier({
                          id: creator.id,
                          username: creator.username,
                        }),
                      },
                    })
                  }
                  className="cursor-pointer bg-zinc-900/40 border border-white/8 rounded-2xl p-4 flex items-center gap-3 hover:border-white/20 transition-all"
                >
                  {creator.profile_image || creator.image ? (
                    <ServiceImage
                      src={creator.profile_image ?? creator.image ?? ''}
                      alt={creator.full_name}
                      className="w-12 h-12 rounded-full object-cover shrink-0"
                      fallbackClassName="w-12 h-12 rounded-full object-contain p-2 shrink-0 bg-zinc-800"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500 shrink-0">
                      <FiUser size={20} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">
                      {creator.full_name || creator.username}
                    </p>
                    <p className="text-zinc-500 text-[12px]">View Profile</p>
                  </div>
                  <FiChevronRight size={18} className="text-zinc-600 shrink-0" />
                </div>
              </div>
            )}

            {/* Apply CTA — visible to non-owners only */}
            {!isOwner && (
              <div className="mt-6">
                <button
                  onClick={handleOpenApply}
                  className="flex items-center gap-2 h-13 px-10 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-[15px] transition-all active:scale-95"
                >
                  <FiMail size={18} />
                  Contact
                </button>
                <p className="text-zinc-600 text-[12px] mt-3">Contact the service provider.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <BottomNav />

      {/* Apply modal */}
      <ApplyServiceModal
        isOpen={isApplyModalOpen}
        onClose={() => setIsApplyModalOpen(false)}
        service={service as unknown as ApplyServiceData}
        serviceId={serviceId}
        user={user}
        isAuthenticated={isAuthenticated}
      />

      {/* Image lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 sm:p-8 cursor-pointer"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            className="absolute top-5 right-5 p-3 bg-black/70 border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-black/90 transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
          >
            <FiX size={24} />
          </button>

          <div
            className="w-full h-full max-w-6xl max-h-screen flex flex-col items-center justify-center relative cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxIndex > 0 && (
              <button
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 p-4 bg-black/60 hover:bg-black/80 border border-white/15 rounded-full flex items-center justify-center text-white transition-all z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((p) => (p !== null ? p - 1 : p));
                }}
              >
                <FiChevronLeft size={32} />
              </button>
            )}

            <ServiceImage
              src={service.images[lightboxIndex]}
              alt={`Image ${lightboxIndex + 1}`}
              className="max-w-[88vw] max-h-[75vh] object-contain rounded-2xl shadow-2xl"
              fallbackClassName="max-w-[88vw] max-h-[75vh] object-contain rounded-2xl shadow-2xl p-8 bg-zinc-900"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />

            {service.images.length > 0 && (
              <div className="mt-4 bg-black/60 backdrop-blur-sm text-white text-sm font-medium px-3 py-1.5 rounded-full z-10 select-none">
                {lightboxIndex + 1} / {service.images.length}
              </div>
            )}

            {lightboxIndex < service.images.length - 1 && (
              <button
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 p-4 bg-black/60 hover:bg-black/80 border border-white/15 rounded-full flex items-center justify-center text-white transition-all z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((p) => (p !== null ? p + 1 : p));
                }}
              >
                <FiChevronRight size={32} />
              </button>
            )}

            {service.images.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {service.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(i);
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === lightboxIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation overlay */}
      {isDeleteConfirming && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-3xl p-6 max-w-sm w-full">
            <h2 className="text-white text-lg font-semibold mb-2">Delete Service?</h2>
            <p className="text-zinc-400 text-sm mb-6">
              <span className="text-white font-medium">"{service.title}"</span> will be permanently
              removed.
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1 rounded-full bg-zinc-900 border-white/5"
                onClick={() => setIsDeleteConfirming(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-full bg-red-600 hover:bg-red-500 text-white"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
